import 'reflect-metadata';

import { StoryCreationService } from '@src/services/StoryCreationService';
import VideoCreatorStore from '@src/stores/VideoCreatorStore';
import { RootStore } from '@src/stores-v2/RootStore';
import { container, type DependencyContainer } from 'tsyringe';

import { DI_TOKENS } from './tokens';

import type { IUploadService } from '@src/services/upload/UploadService';
import type { UploadWorkerProvider } from '@src/services/upload/UploadWorkerProvider';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';

// Re-export tokens for existing import sites
export { DI_TOKENS };

/**
 * Initialize the dependency injection container with all required registrations
 * This should be called once at application startup
 */
export function initializeDIContainer(rootStore?: RootStore): void {
  // If a rootStore instance is provided, register it as a singleton instance
  if (rootStore) {
    container.registerInstance(DI_TOKENS.ROOT_STORE, rootStore);
  } else {
    // Otherwise register RootStore as a singleton class
    container.registerSingleton(DI_TOKENS.ROOT_STORE, RootStore);
  }

  // Register VideoCreatorStore with factory that injects RootStore
  container.register<IVideoCreatorStore>(DI_TOKENS.VIDEO_CREATOR_STORE, {
    useFactory: (c: DependencyContainer) => {
      const root = resolve(DI_TOKENS.ROOT_STORE, c);
      return new VideoCreatorStore(root);
    },
  });

  // Register StoryCreationService (stateless orchestrator)
  container.register(DI_TOKENS.STORY_CREATION_SERVICE, {
    useFactory: (c: DependencyContainer) => {
      const rootStoreInstance = resolve(DI_TOKENS.ROOT_STORE, c);
      const videoCreatorStore = resolve(DI_TOKENS.VIDEO_CREATOR_STORE, c);
      const { datoClientStore, userIdentityStore: userIdStore } =
        rootStoreInstance;
      return new StoryCreationService({
        videoCreator: videoCreatorStore,
        datoClientStore,
        userIdentity: userIdStore,
      });
    },
  });

  try {
    // Dynamic import to avoid forcing upload code into bundles/tests not using it
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const {
      createDefaultUploadWorkerProvider,
    } = require('@src/services/upload/UploadWorkerProvider');
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { UploadService } = require('@src/services/upload/UploadService');
    // Register worker provider as a value (lazy worker construction happens inside provider)
    container.register(DI_TOKENS.UPLOAD_WORKER_PROVIDER, {
      useValue: createDefaultUploadWorkerProvider(),
    });
    // IMPORTANT: Use explicit factory instead of registerSingleton(class) to avoid tsyringe
    // requiring design:type metadata (which can fail in some dynamic require / build edge cases)
    container.register(DI_TOKENS.UPLOAD_SERVICE, {
      useFactory: (c: DependencyContainer) => {
        try {
          const workerProvider = c.resolve(DI_TOKENS.UPLOAD_WORKER_PROVIDER);
          return new UploadService(workerProvider);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('UploadService factory failed', err);
          return undefined;
        }
      },
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      'Warning: Failed to initialize upload dependencies. Uploads will not function.',
      err,
    );
  }
}

/**
 * Get the DI container instance
 * Use this to resolve dependencies when needed
 */
export function getDIContainer() {
  return container;
}

type TokenMap = {
  [DI_TOKENS.VIDEO_CREATOR_STORE]: IVideoCreatorStore;
  [DI_TOKENS.ROOT_STORE]: RootStore;
  [DI_TOKENS.STORY_CREATION_SERVICE]: StoryCreationService;
  // Upload tokens are optional in some test slices; mark as any to avoid hard dependency when not initialized.
  // They will exist whenever initializeDIContainer runs in the real app bundle.
  // These may be absent in certain isolated test bundles that never call initializeDIContainer upload branch.
  [DI_TOKENS.UPLOAD_WORKER_PROVIDER]: UploadWorkerProvider | undefined;
  [DI_TOKENS.UPLOAD_SERVICE]: IUploadService | undefined;
};

/**
 * Resolve a dependency from the container
 * @param token - The token to resolve
 */
export function resolve<T extends keyof TokenMap>(
  token: T,
  c?: typeof container,
): TokenMap[T] {
  if (c === undefined) return container.resolve<TokenMap[T]>(token);
  else return c.resolve<TokenMap[T]>(token);
}

/**
 * Clear all instances from the container
 * Useful for testing scenarios
 */
export function clearDIContainer(): void {
  container.clearInstances();
}
